import { create } from 'zustand'
import { toast } from 'sonner'
import { billingApi } from '../lib/api'
import { captureEvent } from '../lib/posthog'
import type {
  Plan,
  SubscriptionData,
  CreditBalanceData,
  CreditPack,
  CreditTransactionData,
  DailyUsageItem,
  UsageBreakdownItem,
  UsageTrendItem,
  UsageSummaryItem,
  BillingSettings,
  EstimateResponse,
  EstimateRequestPayload,
  BillingCycle,
  TeamCreditUsageRow,
} from '../types/billing'

interface BillingState {
  // Data
  plans: Plan[]
  subscription: SubscriptionData | null
  credits: CreditBalanceData | null
  creditsLoaded: boolean
  creditPacks: CreditPack[]
  transactions: CreditTransactionData[]
  transactionsTotal: number
  summary: UsageSummaryItem[]
  dailyUsage: DailyUsageItem[]
  breakdown: UsageBreakdownItem[]
  trends: UsageTrendItem[]
  settings: BillingSettings | null
  /** GET /credit-budgets/usage — the per-teammate credit table. */
  teamUsage: TeamCreditUsageRow[]
  isLoading: boolean

  // Actions
  fetchPlans: () => Promise<void>
  fetchSubscription: () => Promise<void>
  /** Buys the plan as a monthly subscription (the only plan product). `seats`
   *  is the paid-seat count for a per-seat plan (Momentum: 2-5). */
  switchPlan: (planId: number, billingCycle?: BillingCycle, seats?: number) => Promise<void>
  /** Buy `additional` seats now (prorated). Resolves true on success. */
  addSeats: (additional: number) => Promise<boolean>
  /** Schedule fewer seats from the next renewal. Resolves true on success. */
  reduceSeats: (seats: number) => Promise<boolean>
  cancelSeatReduction: () => Promise<boolean>
  fetchTeamUsage: () => Promise<void>
  fetchCredits: () => Promise<void>
  fetchCreditPacks: () => Promise<void>
  fetchTransactions: (skip?: number, limit?: number, action?: string) => Promise<void>
  fetchSummary: () => Promise<void>
  fetchDailyUsage: (days?: number) => Promise<void>
  fetchBreakdown: () => Promise<void>
  fetchTrends: () => Promise<void>
  fetchSettings: () => Promise<void>
  createCheckoutSession: (planId: number) => Promise<string | null>
  changePlan: (newPlanId: number) => Promise<void>
  cancelSubscription: () => Promise<void>
  reactivateSubscription: () => Promise<void>
  purchaseCredits: (creditPackId: number) => Promise<void>
  updateSettings: (data: Partial<BillingSettings>) => Promise<void>
  estimate: (data: EstimateRequestPayload) => Promise<EstimateResponse | null>

  // Reset — wipes per-user billing data on account switch (see main.tsx auth
  // subscribe). plans/creditPacks are catalog data, not per-user, but clearing
  // them only forces a harmless re-fetch on the next Billing page mount.
  reset: () => void
}

const initialState = {
  plans: [] as Plan[],
  subscription: null as SubscriptionData | null,
  credits: null as CreditBalanceData | null,
  creditsLoaded: false,
  creditPacks: [] as CreditPack[],
  transactions: [] as CreditTransactionData[],
  transactionsTotal: 0,
  summary: [] as UsageSummaryItem[],
  dailyUsage: [] as DailyUsageItem[],
  breakdown: [] as UsageBreakdownItem[],
  trends: [] as UsageTrendItem[],
  settings: null as BillingSettings | null,
  teamUsage: [] as TeamCreditUsageRow[],
  isLoading: false,
}

export const useBillingStore = create<BillingState>()((set, get) => ({
  ...initialState,

  fetchPlans: async () => {
    try {
      const { data } = await billingApi.getPlans()
      set({ plans: data })
    } catch {
      toast.error('Failed to load plans')
    }
  },

  fetchSubscription: async () => {
    try {
      const { data } = await billingApi.getSubscription()
      set({ subscription: data.subscription })
    } catch (error: any) {
      // 403 is an EXPECTED answer, not a failure: most roles have no billing
      // cell, and a stale permission map can briefly mount a billing consumer
      // for them (seen live when an invitee who had just been a solo owner
      // accepted an invite — the toast fired before their narrower map
      // landed). Their true state IS "no subscription visible".
      if (error?.response?.status === 403) {
        set({ subscription: null })
        return
      }
      toast.error('Failed to load subscription')
    }
  },

  switchPlan: async (planId: number, billingCycle: BillingCycle = 'monthly', seats?: number) => {
    try {
      set({ isLoading: true })
      const { data } = await billingApi.switchPlan({
        plan_id: planId,
        success_url: `${window.location.origin}/billing?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/billing`,
        billing_cycle: billingCycle,
        ...(seats !== undefined ? { seats } : {}),
      })
      // 'queued' is a lower plan lined up behind the current one (no-downgrade
      // flow): Stripe Checkout still runs to authorise the card, but the charge
      // is anchored to the current plan's expiry, so it starts then, not today.
      if ((data.action === 'payment_required' || data.action === 'queued') && data.url) {
        window.location.href = data.url
        return
      }
      if (data.action === 'resumed') {
        // The backend revived the winding-down agreement instead of opening a
        // second one, so nothing was charged today.
        toast.success('Subscription resumed. Nothing charged today.')
      } else if (data.action === 'already_active') {
        toast.info('That plan is already active.')
      }
      await Promise.all([get().fetchSubscription(), get().fetchCredits()])
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to switch plan')
    } finally {
      set({ isLoading: false })
    }
  },

  // Seats (Momentum per-seat spec). Each refreshes the subscription (which
  // carries the seat block) so P/U on screen never lag the change. They
  // resolve a boolean instead of throwing so a modal can stay open on failure
  // without every caller needing its own try/catch; the toast is here.
  addSeats: async (additional: number) => {
    try {
      set({ isLoading: true })
      const { data } = await billingApi.addSeats(additional)
      toast.success(
        `${data.added} seat${data.added === 1 ? '' : 's'} added. You now pay for ${data.paid_seats}. `
        + 'The rest of this period was charged today.',
      )
      captureEvent('seats_added', { additional, paid_seats: data.paid_seats })
      await get().fetchSubscription()
      return true
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to add seats')
      return false
    } finally {
      set({ isLoading: false })
    }
  },

  reduceSeats: async (seats: number) => {
    try {
      set({ isLoading: true })
      const { data } = await billingApi.reduceSeats(seats)
      toast.success(`From your next renewal you will pay for ${data.pending_paid_seats} seats. Nothing changes until then.`)
      captureEvent('seats_reduction_scheduled', { seats })
      await get().fetchSubscription()
      return true
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reduce seats')
      return false
    } finally {
      set({ isLoading: false })
    }
  },

  cancelSeatReduction: async () => {
    try {
      set({ isLoading: true })
      await billingApi.cancelSeatReduction()
      toast.success('Seat reduction cancelled. Your seat count is unchanged.')
      await get().fetchSubscription()
      return true
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to cancel the seat reduction')
      return false
    } finally {
      set({ isLoading: false })
    }
  },

  fetchTeamUsage: async () => {
    try {
      const { data } = await billingApi.getTeamCreditUsage()
      set({ teamUsage: Array.isArray(data) ? data : [] })
    } catch (error: any) {
      // 403 = the plan does not include per-user budgets (Starter) or the role
      // cannot see the list; both are "nothing to show", not failures.
      if (error?.response?.status === 403) {
        set({ teamUsage: [] })
        return
      }
      toast.error('Failed to load team credit usage')
    }
  },

  fetchCredits: async () => {
    try {
      const { data } = await billingApi.getCredits()
      set({ credits: data.credits })
    } catch {
      toast.error('Failed to load credits')
    } finally {
      // Mark the fetch as attempted so the UI can distinguish "loading" from
      // "loaded but no active balance" (credits === null) and not hang.
      set({ creditsLoaded: true })
    }
  },

  fetchCreditPacks: async () => {
    try {
      const { data } = await billingApi.getCreditPacks()
      set({ creditPacks: data })
    } catch {
      toast.error('Failed to load credit packs')
    }
  },

  fetchTransactions: async (skip = 0, limit = 50, action?: string) => {
    try {
      const { data } = await billingApi.getTransactions({ skip, limit, action })
      set({ transactions: data.transactions, transactionsTotal: data.total })
    } catch {
      toast.error('Failed to load transactions')
    }
  },

  fetchSummary: async () => {
    try {
      const { data } = await billingApi.getTransactionSummary()
      set({ summary: data.summary })
    } catch {
      toast.error('Failed to load summary')
    }
  },

  fetchDailyUsage: async (days = 30) => {
    try {
      const { data } = await billingApi.getUsageDaily(days)
      set({ dailyUsage: data.daily })
    } catch {
      toast.error('Failed to load daily usage')
    }
  },

  fetchBreakdown: async () => {
    try {
      const { data } = await billingApi.getUsageBreakdown()
      set({ breakdown: data.breakdown })
    } catch {
      toast.error('Failed to load breakdown')
    }
  },

  fetchTrends: async () => {
    try {
      const { data } = await billingApi.getUsageTrends()
      set({ trends: data.trends })
    } catch {
      toast.error('Failed to load trends')
    }
  },

  fetchSettings: async () => {
    try {
      const { data } = await billingApi.getSettings()
      set({ settings: data.settings })
    } catch {
      toast.error('Failed to load billing settings')
    }
  },

  createCheckoutSession: async (planId: number) => {
    try {
      set({ isLoading: true })
      const { data } = await billingApi.createCheckoutSession({
        plan_id: planId,
        success_url: `${window.location.origin}/billing?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/billing`,
      })
      return data.url
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session')
      return null
    } finally {
      set({ isLoading: false })
    }
  },

  changePlan: async (newPlanId: number) => {
    try {
      set({ isLoading: true })
      const { data } = await billingApi.changePlan(newPlanId)
      if (data.action === 'upgraded') {
        toast.success(`Upgraded to ${data.plan}! ${data.credits_added} credits added.`)
      } else {
        toast.success(`Downgrade to ${data.plan} scheduled for ${data.effective}`)
      }
      await get().fetchSubscription()
      await get().fetchCredits()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to change plan')
    } finally {
      set({ isLoading: false })
    }
  },

  cancelSubscription: async () => {
    try {
      set({ isLoading: true })
      const { data } = await billingApi.cancelSubscription()
      // The backend answers "noop" when there is no recurring agreement (a
      // one-off pass) — saying "cancelled" there would be a lie, since the
      // plan was always going to expire on its own.
      if (data?.status === 'noop') {
        toast.info('This plan already expires on its own. Nothing to cancel.')
      } else {
        toast.success('Auto-renew is off. Your plan stays active until it expires.')
      }
      await get().fetchSubscription()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription')
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  reactivateSubscription: async () => {
    try {
      set({ isLoading: true })
      const { data } = await billingApi.reactivateSubscription()
      if (data?.status === 'noop') {
        toast.info('There is no scheduled cancellation to undo.')
      } else {
        toast.success('Auto-renew is back on.')
      }
      await get().fetchSubscription()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to resume subscription')
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  purchaseCredits: async (creditPackId: number) => {
    try {
      set({ isLoading: true })
      const { data } = await billingApi.purchaseCredits({
        credit_pack_id: creditPackId,
        success_url: `${window.location.origin}/billing?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/billing`,
      })
      captureEvent('credits_purchase_started', { credit_pack_id: creditPackId })
      if (data.url) window.location.href = data.url
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to start credit purchase')
    } finally {
      set({ isLoading: false })
    }
  },

  updateSettings: async (data: Partial<BillingSettings>) => {
    try {
      await billingApi.updateSettings(data)
      toast.success('Billing settings updated')
      await get().fetchSettings()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update settings')
    }
  },

  estimate: async (data) => {
    try {
      const { data: result } = await billingApi.estimate(data)
      return result
    } catch {
      toast.error('Failed to estimate credits')
      return null
    }
  },

  reset: () => set(initialState),
}))
